import React, { useEffect } from "react";
import {
  Image,
  message,
  Select,
  Table,
  TableColumnsType,
  Typography,
} from "antd";
import { TransactionHistoryFilter, Transaction } from "common/interfaces";
import PaginateListMobile from "components/PaginateListMobile";
import { usePagination } from "common/hooks/usePagination";
import { useGetTransactionHistoriesQuery } from "reducers/transactionApi";
import { parseErrorMessage, productPrice } from "common/utils";
import { Link } from "react-router-dom";
import { UTCToJapanTime } from "common/helper/time.helper";
import { Controller, useForm } from "react-hook-form";
import TransactionHistoryItem from "components/TransactionHistoryItem";
import { isEmpty } from "lodash";
import { useCommonContext } from "contexts/CommonContext";
import { FILE_BASE_URL } from "config/apiConfig";
import { PRODUCT_CATEGORY } from "common/constants";
import { TransactionStatusEnum } from "common/enums/TransactionStatus.enum";

const columns: TableColumnsType<Transaction> = [
  {
    title: "出品ID",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Transaction) => (
      <Link
        to={`/seller/product/${record.product.code}`}
        className="text-primary"
      >
        #{record.product.code}
      </Link>
    ),
  },
  {
    title: "商品名",
    dataIndex: "product",
    align: "center",
    render: (value) => (
      <div className="flex items-center gap-[10px] self-stretch">
        <Image
          src={`${FILE_BASE_URL}/${value.image_url}`}
          className="!w-[40px] !h-[40px] object-cover"
        />
        <Typography>{value.product_name}</Typography>
      </div>
    ),
  },
  {
    title: "買い手企業",
    dataIndex: "buyer",
    align: "center",
    render: (value) => value.company_name,
  },
  {
    title: "販売価格",
    dataIndex: "product",
    align: "center",
    render: (value) => productPrice(value),
  },
  {
    title: "取引完了日",
    dataIndex: "created_at",
    align: "center",
    render: (value) => {
      const timeJapan = UTCToJapanTime(value);
      return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
    },
  },
  {
    title: "詳細画面",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Transaction) => (
      <>
        <Link
          to={`/seller/product/${record.product.code}`}
          className="text-primary"
        >
          商品詳細画面へ
        </Link>
      </>
    ),
  },
];

const PastListHistory = () => {
  const { setHeaderTitle } = useCommonContext();
  const defaultValues: TransactionHistoryFilter = {
    search: "",
    type: [],
    status: [TransactionStatusEnum.COMPLETED.toString()],
    sort: "",
    deleted_at: null,
  };
  const { control, handleSubmit } = useForm<TransactionHistoryFilter>({
    defaultValues,
  });
  const {
    pagination,
    paginationMobile,
    onChangePage,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const onSubmit = async () => {
    console.log();
  };

  const { data: transactionHistoryData, error: inquiryErrClose } =
    useGetTransactionHistoriesQuery({
      filter: defaultValues,
      page: pagination.current,
    });

  const { data: transactionHistoryMobileData, error: inquiryMobileErrClose } =
    useGetTransactionHistoriesQuery({
      filter: defaultValues,
      page: paginationMobile.current,
    });

  useEffect(() => {
    if (transactionHistoryData?.data?.meta?.total_items) {
      updateTotal(transactionHistoryData.data.meta.total_items);
    }
  }, [transactionHistoryData]);

  useEffect(() => {
    if (transactionHistoryMobileData?.data?.meta?.total_items) {
      updateTotalMobile(transactionHistoryMobileData.data.meta.total_items);
    }
  }, [transactionHistoryMobileData]);

  useEffect(() => {
    if (inquiryErrClose) void message.error(parseErrorMessage(inquiryErrClose));
  }, [inquiryErrClose]);

  useEffect(() => {
    if (inquiryMobileErrClose)
      void message.error(parseErrorMessage(inquiryMobileErrClose));
  }, [inquiryMobileErrClose]);

  useEffect(() => {
    setHeaderTitle("過去の出品履歴の閲覧");
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="w-full self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[20px] md:gap-10 overflow-hidden">
        <div className="self-stretch flex flex-col justify-start items-start gap-7">
          <div className="self-stretch flex flex-col justify-start items-start gap-5">
            <div className="inline-flex justify-start items-center gap-5">
              <div className="justify-start text-black text-[15px] md:text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                表示期間
              </div>
              <div className="min-w-[200px] flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <Select
                      {...field}
                      allowClear
                      options={Object.values(PRODUCT_CATEGORY)}
                      placeholder="全期間"
                      className="w-full h-[50px]"
                    />
                  )}
                />
              </div>
            </div>
            <Table<Transaction>
              className="w-full hidden md:block"
              rowKey="code"
              onChange={onChangePage}
              pagination={
                !isEmpty(transactionHistoryData?.data?.items)
                  ? pagination
                  : false
              }
              columns={columns}
              dataSource={transactionHistoryData?.data?.items || []}
            />
            <PaginateListMobile<Transaction>
              pagination={
                !isEmpty(transactionHistoryMobileData?.data?.items)
                  ? paginationMobile
                  : false
              }
              dataSource={transactionHistoryMobileData?.data?.items || []}
              renderItem={(item, checked, onSelect) => (
                <TransactionHistoryItem<Transaction>
                  transaction={item}
                  checked={checked}
                  onSelect={onSelect}
                  hideSelect={true}
                />
              )}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default PastListHistory;
